/**
 * useFieldMaterialFeedback — Phase 4 read-only hook.
 *
 * Returns the canonical (append-only) field material feedback events for
 * a job, partitioned and counted by severity. Pure read — never writes.
 * Mutations flow through `useProcurementWriters()` only.
 */
import { useMemo } from "react";
import { useVendorPO, type FieldMaterialFeedbackEvent } from "@/data/VendorPOContext";

export interface FieldFeedbackBucket {
  events: FieldMaterialFeedbackEvent[];
  unresolved: FieldMaterialFeedbackEvent[];
  resolved: FieldMaterialFeedbackEvent[];
  unresolvedBlockers: FieldMaterialFeedbackEvent[];
  unresolvedWarns: FieldMaterialFeedbackEvent[];
  unresolvedInfos: FieldMaterialFeedbackEvent[];
  hasUnresolvedBlocker: boolean;
  hasUnresolvedWarn: boolean;
  total: number;
  unresolvedCount: number;
}

const EMPTY: FieldFeedbackBucket = {
  events: [],
  unresolved: [],
  resolved: [],
  unresolvedBlockers: [],
  unresolvedWarns: [],
  unresolvedInfos: [],
  hasUnresolvedBlocker: false,
  hasUnresolvedWarn: false,
  total: 0,
  unresolvedCount: 0,
};

export function useFieldMaterialFeedback(jobId: string | null | undefined): FieldFeedbackBucket {
  const { fieldMaterialFeedback } = useVendorPO();
  return useMemo(() => {
    if (!jobId) return EMPTY;
    const events = (fieldMaterialFeedback ?? []).filter((e) => e.jobId === jobId);
    if (events.length === 0) return EMPTY;
    const unresolved = events.filter((e) => !e.resolved);
    const resolved = events.filter((e) => e.resolved);
    const unresolvedBlockers = unresolved.filter((e) => e.severity === "blocker");
    const unresolvedWarns = unresolved.filter((e) => e.severity === "warn");
    const unresolvedInfos = unresolved.filter((e) => e.severity === "info");
    return {
      events,
      unresolved,
      resolved,
      unresolvedBlockers,
      unresolvedWarns,
      unresolvedInfos,
      hasUnresolvedBlocker: unresolvedBlockers.length > 0,
      hasUnresolvedWarn: unresolvedWarns.length > 0,
      total: events.length,
      unresolvedCount: unresolved.length,
    };
  }, [fieldMaterialFeedback, jobId]);
}
